import { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Button, Steps, Spin, message,
  Select, Modal, Descriptions, Timeline, Progress, Space, Alert, Divider,
} from 'antd';
import {
  FileSearchOutlined, CheckCircleOutlined, ThunderboltOutlined,
  BarChartOutlined, AccountBookOutlined, RobotOutlined, PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import { vppTradeCycle } from '../../../services/api';

const STEP_ITEMS = [
  { title: '申报曲线', icon: <FileSearchOutlined /> },
  { title: '出清结果', icon: <CheckCircleOutlined /> },
  { title: '执行跟踪', icon: <ThunderboltOutlined /> },
  { title: '响应评估', icon: <BarChartOutlined /> },
  { title: '交易结算', icon: <AccountBookOutlined /> },
];

const STATUS_COLORS = {
  '申报中': 'blue', '已出清': 'cyan', '执行中': 'orange',
  '已评估': 'purple', '已结算': 'green',
};

function bidCurveOption(declaration, clearing) {
  const hours = (declaration?.curve || []).map((p) => p.hour);
  const declared = (declaration?.curve || []).map((p) => p.quantity_mw);
  const aiCurve = (declaration?.ai_recommended || []).map((p) => p.quantity_mw);
  const cleared = (clearing?.hourly || []).map((p) => p.cleared_mw);
  const series = [
    { name: '申报电量', type: 'bar', data: declared, itemStyle: { color: '#1890ff' } },
    { name: 'AI推荐', type: 'line', smooth: true, data: aiCurve, itemStyle: { color: '#722ed1' } },
  ];
  if (cleared.length) {
    series.push({ name: '中标电量', type: 'bar', data: cleared, itemStyle: { color: '#52c41a' } });
  }
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: series.map((s) => s.name) },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: hours },
    yAxis: { type: 'value', name: 'MW' },
    series,
  };
}

function priceCurveOption(declaration, clearing) {
  const hours = (declaration?.curve || []).map((p) => p.hour);
  const declaredPrice = (declaration?.curve || []).map((p) => p.price_yuan_per_kwh);
  const clearedPrice = (clearing?.hourly || []).map((p) => p.cleared_price);
  const series = [
    { name: '申报价格', type: 'line', smooth: true, data: declaredPrice, itemStyle: { color: '#fa8c16' } },
  ];
  if (clearedPrice.length) {
    series.push({ name: '出清价格', type: 'line', smooth: true, data: clearedPrice, itemStyle: { color: '#52c41a' } });
  }
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: series.map((s) => s.name) },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: hours },
    yAxis: { type: 'value', name: '元/kWh' },
    series,
  };
}

function executionOption(execution) {
  if (!execution?.units) return {};
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['目标功率', '实际功率'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: execution.units.map((u) => u.unit_id) },
    yAxis: { type: 'value', name: 'MW' },
    series: [
      { name: '目标功率', type: 'bar', data: execution.units.map((u) => u.target_mw), itemStyle: { color: '#1890ff' } },
      { name: '实际功率', type: 'bar', data: execution.units.map((u) => u.actual_mw), itemStyle: { color: '#52c41a' } },
    ],
  };
}

function settlementPieOption(settlement) {
  if (!settlement?.sharing) return {};
  const s = settlement.sharing;
  return {
    tooltip: { trigger: 'item', formatter: '{b}: {c} 元 ({d}%)' },
    series: [{
      type: 'pie', radius: ['40%', '70%'],
      data: [
        { name: '平台分成', value: s.platform.amount, itemStyle: { color: '#1890ff' } },
        { name: '资源方分成', value: s.resource_owners.amount, itemStyle: { color: '#52c41a' } },
        { name: '风险准备金', value: s.reserve.amount, itemStyle: { color: '#faad14' } },
      ],
      label: { formatter: '{b}\n{d}%' },
    }],
  };
}

export default function VppTradeLoop() {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [marketType, setMarketType] = useState('日前市场');
  const [aiPreview, setAiPreview] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [ovRes, listRes] = await Promise.all([
        vppTradeCycle.overview(),
        vppTradeCycle.list(),
      ]);
      setOverview(ovRes.data);
      const list = listRes.data || [];
      setCycles(list);
      if (selected) {
        const updated = list.find((c) => c.cycle_id === selected.cycle_id);
        setSelected(updated || list[0] || null);
      } else if (list.length) {
        setSelected(list[0]);
      }
    } catch (err) {
      message.error(err.message || '加载交易闭环数据失败');
    } finally {
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => { loadData(); }, []);

  const loadAiPreview = async (type) => {
    try {
      const res = await vppTradeCycle.aiCurve({ market_type: type });
      setAiPreview(res.data);
    } catch {
      setAiPreview(null);
    }
  };

  useEffect(() => {
    if (createOpen) loadAiPreview(marketType);
  }, [createOpen, marketType]);

  const handleCreate = async () => {
    setActionLoading(true);
    try {
      const res = await vppTradeCycle.create({ market_type: marketType, use_ai: true });
      message.success(res.message || '申报已提交');
      setCreateOpen(false);
      setSelected(res.data);
      await loadData();
    } catch (err) {
      message.error(err.message || '申报失败');
    } finally {
      setActionLoading(false);
    }
  };

  const advance = async (action, label) => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const res = await vppTradeCycle[action](selected.cycle_id);
      message.success(res.message || `${label}完成`);
      setSelected(res.data);
      await loadData();
    } catch (err) {
      message.error(err.message || `${label}失败`);
    } finally {
      setActionLoading(false);
    }
  };

  const nextAction = selected ? {
    1: { fn: 'clearing', label: '模拟出清', step: '出清结果' },
    2: { fn: 'execution', label: '启动执行跟踪', step: '执行跟踪' },
    3: { fn: 'evaluation', label: '生成响应评估', step: '响应评估' },
    4: { fn: 'settlement', label: '完成交易结算', step: '交易结算' },
  }[selected.current_step] : null;

  const listColumns = [
    { title: '交易编号', dataIndex: 'cycle_id', width: 180 },
    { title: '市场类型', dataIndex: 'market_type', render: (t) => <Tag>{t}</Tag> },
    { title: '交易日期', dataIndex: 'trade_date' },
    { title: '当前步骤', dataIndex: 'current_step', render: (s) => STEP_ITEMS[s - 1]?.title || s },
    { title: '状态', dataIndex: 'status', render: (s) => <Tag color={STATUS_COLORS[s]}>{s}</Tag> },
    {
      title: '操作',
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => setSelected(record)}>查看</Button>
      ),
    },
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const decl = selected?.declaration_data;
  const clearing = selected?.clearing_data;
  const execution = selected?.execution_data;
  const evaluation = selected?.evaluation_data;
  const settlement = selected?.settlement_data;

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="VPP 五步交易闭环"
        description="申报曲线 → 出清结果 → 执行跟踪 → 响应评估 → 交易结算。对标虚拟电厂全流程运营，AI 辅助报价策略，评估结果自动反馈至下轮申报。"
      />

      {overview && (
        <Row gutter={[16, 16]}>
          <Col span={4}><Card size="small"><Statistic title="交易周期" value={overview.total_cycles} suffix="个" /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="进行中" value={overview.active_cycles} suffix="个" valueStyle={{ color: '#faad14' }} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="已结算" value={overview.settled_cycles} suffix="个" valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="累计收益" value={overview.total_revenue} suffix="元" precision={0} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="平均出清率" value={overview.avg_clearing_rate} suffix="%" precision={1} /></Card></Col>
          <Col span={4}><Card size="small"><Statistic title="平均履约率" value={overview.avg_compliance} suffix="%" precision={1} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        </Row>
      )}

      <Card
        title="交易闭环流程"
        style={{ marginTop: 16 }}
        size="small"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            新建申报
          </Button>
        }
      >
        <Steps
          current={(selected?.current_step || 1) - 1}
          items={STEP_ITEMS.map((item) => ({ title: item.title, icon: item.icon }))}
          style={{ marginBottom: 24 }}
        />
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={10}>
          <Card title="交易周期列表" size="small">
            <Table
              columns={listColumns}
              dataSource={cycles.map((c) => ({ ...c, key: c.cycle_id }))}
              pagination={{ pageSize: 5, size: 'small' }}
              size="small"
              rowClassName={(r) => r.cycle_id === selected?.cycle_id ? 'ant-table-row-selected' : ''}
            />
          </Card>
        </Col>
        <Col span={14}>
          {selected ? (
            <Card
              title={`${selected.cycle_id} · ${selected.market_type}`}
              size="small"
              extra={
                nextAction ? (
                  <Button
                    type="primary"
                    icon={<RightOutlined />}
                    loading={actionLoading}
                    onClick={() => advance(nextAction.fn, nextAction.label)}
                  >
                    {nextAction.label}
                  </Button>
                ) : selected.current_step >= 5 ? (
                  <Tag color="green">闭环已完成</Tag>
                ) : null
              }
            >
              <Descriptions size="small" column={3} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="交易日期">{selected.trade_date}</Descriptions.Item>
                <Descriptions.Item label="聚合容量">{selected.total_capacity_mw} MW</Descriptions.Item>
                <Descriptions.Item label="状态"><Tag color={STATUS_COLORS[selected.status]}>{selected.status}</Tag></Descriptions.Item>
                {selected.ai_strategy_note && (
                  <Descriptions.Item label="AI策略" span={3}>
                    <RobotOutlined style={{ color: '#722ed1', marginRight: 4 }} />
                    {selected.ai_strategy_note}
                  </Descriptions.Item>
                )}
              </Descriptions>

              {/* Step 1: 申报 */}
              {decl && (
                <>
                  <Divider orientation="left" plain>① 申报曲线</Divider>
                  <Row gutter={16}>
                    <Col span={8}><Statistic title="申报总量" value={decl.summary?.total_quantity_mwh} suffix="MWh" precision={1} /></Col>
                    <Col span={8}><Statistic title="均价" value={decl.summary?.avg_price} suffix="元/kWh" precision={4} /></Col>
                    <Col span={8}><Statistic title="预期收益" value={decl.summary?.expected_revenue} suffix="元" precision={0} valueStyle={{ color: '#1890ff' }} /></Col>
                  </Row>
                  <ReactEChartsCore option={bidCurveOption(decl, clearing)} style={{ height: 220, marginTop: 8 }} />
                </>
              )}

              {/* Step 2: 出清 */}
              {clearing && (
                <>
                  <Divider orientation="left" plain>② 出清结果</Divider>
                  <Row gutter={16}>
                    <Col span={6}><Statistic title="中标总量" value={clearing.total_cleared_mwh} suffix="MWh" precision={1} /></Col>
                    <Col span={6}><Statistic title="出清均价" value={clearing.avg_cleared_price} suffix="元/kWh" precision={4} /></Col>
                    <Col span={6}><Statistic title="成交金额" value={clearing.total_amount} suffix="元" precision={0} valueStyle={{ color: '#52c41a' }} /></Col>
                    <Col span={6}><Statistic title="出清率" value={clearing.avg_clearing_rate} suffix="%" precision={1} /></Col>
                  </Row>
                  <ReactEChartsCore option={priceCurveOption(decl, clearing)} style={{ height: 200, marginTop: 8 }} />
                </>
              )}

              {/* Step 3: 执行 */}
              {execution && (
                <>
                  <Divider orientation="left" plain>③ 执行跟踪（多机组）</Divider>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={execution.units.map((u) => ({ ...u, key: u.unit_id }))}
                    columns={[
                      { title: '机组', dataIndex: 'unit_id' },
                      { title: '类型', dataIndex: 'unit_type' },
                      { title: '资源', dataIndex: 'resource_type' },
                      { title: '目标(MW)', dataIndex: 'target_mw' },
                      { title: '实际(MW)', dataIndex: 'actual_mw' },
                      { title: '偏差(MW)', dataIndex: 'deviation_mw', render: (v) => <span style={{ color: v < 0 ? '#ff4d4f' : '#52c41a' }}>{v}</span> },
                      { title: '履约率', dataIndex: 'compliance_rate', render: (v) => <Progress percent={v} size="small" status={v >= 90 ? 'success' : 'exception'} /> },
                    ]}
                  />
                  <ReactEChartsCore option={executionOption(execution)} style={{ height: 200, marginTop: 8 }} />
                  {execution.timeline?.length > 0 && (
                    <Timeline
                      style={{ marginTop: 12 }}
                      items={execution.timeline.map((t) => ({
                        children: <><b>{t.time}</b> {t.event} — {t.power_mw} MW</>,
                      }))}
                    />
                  )}
                </>
              )}

              {/* Step 4: 评估 */}
              {evaluation && (
                <>
                  <Divider orientation="left" plain>④ 响应评估</Divider>
                  <Row gutter={16}>
                    <Col span={6}><Statistic title="响应速率" value={evaluation.response_rate} suffix="%" precision={1} /></Col>
                    <Col span={6}><Statistic title="平均履约" value={evaluation.avg_compliance} suffix="%" precision={1} valueStyle={{ color: evaluation.result === '通过' ? '#52c41a' : '#faad14' }} /></Col>
                    <Col span={6}><Statistic title="激励金额" value={evaluation.incentive_amount} suffix="元" precision={0} valueStyle={{ color: '#52c41a' }} /></Col>
                    <Col span={6}><Statistic title="考核扣款" value={evaluation.penalty_amount} suffix="元" precision={0} valueStyle={{ color: '#ff4d4f' }} /></Col>
                  </Row>
                  <Alert
                    style={{ marginTop: 12 }}
                    type={evaluation.result === '通过' ? 'success' : 'warning'}
                    message={`考核结果：${evaluation.result}`}
                    description={
                      <div>
                        <div>{evaluation.ai_feedback}</div>
                        <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                          策略调整建议：报价 {evaluation.strategy_adjustment?.price_adjust}，容量 {evaluation.strategy_adjustment?.capacity_adjust}
                        </div>
                      </div>
                    }
                    showIcon
                  />
                  <Table
                    size="small"
                    style={{ marginTop: 12 }}
                    pagination={false}
                    dataSource={evaluation.unit_scores.map((u) => ({ ...u, key: u.unit_id }))}
                    columns={[
                      { title: '机组', dataIndex: 'unit_id' },
                      { title: '评分', dataIndex: 'score' },
                      { title: '等级', dataIndex: 'grade', render: (g) => <Tag color={g === 'A' ? 'green' : g === 'B' ? 'blue' : 'orange'}>{g}</Tag> },
                    ]}
                  />
                </>
              )}

              {/* Step 5: 结算 */}
              {settlement && (
                <>
                  <Divider orientation="left" plain>⑤ 交易结算</Divider>
                  <Row gutter={16}>
                    <Col span={6}><Statistic title="市场收益" value={settlement.market_revenue} suffix="元" precision={0} /></Col>
                    <Col span={6}><Statistic title="需求响应激励" value={settlement.demand_response_incentive} suffix="元" precision={0} /></Col>
                    <Col span={6}><Statistic title="总收入" value={settlement.total_revenue} suffix="元" precision={0} valueStyle={{ color: '#1890ff', fontWeight: 'bold' }} /></Col>
                    <Col span={6}><Statistic title="净利润" value={settlement.net_profit} suffix="元" precision={0} valueStyle={{ color: '#52c41a', fontWeight: 'bold' }} /></Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={12}>
                      <ReactEChartsCore option={settlementPieOption(settlement)} style={{ height: 220 }} />
                    </Col>
                    <Col span={12}>
                      <Table
                        size="small"
                        pagination={false}
                        dataSource={(settlement.breakdown_by_unit || []).map((u) => ({ ...u, key: u.unit_id }))}
                        columns={[
                          { title: '机组', dataIndex: 'unit_id' },
                          { title: '贡献比例', dataIndex: 'contribution_ratio', render: (v) => `${(v * 100).toFixed(0)}%` },
                          { title: '分成收入(元)', dataIndex: 'income', render: (v) => v.toLocaleString() },
                        ]}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Card>
          ) : (
            <Card size="small"><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无交易周期，点击「新建申报」开始五步闭环</div></Card>
          )}
        </Col>
      </Row>

      <Modal
        title="新建交易申报"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={actionLoading}
        okText="提交申报"
        width={560}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div style={{ marginBottom: 8 }}>市场类型</div>
            <Select
              style={{ width: '100%' }}
              value={marketType}
              onChange={setMarketType}
              options={[
                { value: '日前市场', label: '日前市场' },
                { value: '实时市场', label: '实时市场' },
                { value: '双边协商', label: '双边协商' },
                { value: '需求响应', label: '需求响应' },
              ]}
            />
          </div>
          {aiPreview && (
            <Alert
              type="info"
              icon={<RobotOutlined />}
              message="AI 推荐申报策略"
              description={
                <div>
                  <div>{aiPreview.summary.strategy}</div>
                  <div style={{ marginTop: 8 }}>
                    预计申报 {aiPreview.summary.total_quantity_mwh} MWh，
                    均价 {aiPreview.summary.avg_price} 元/kWh，
                    预期收益约 {aiPreview.summary.expected_revenue.toLocaleString()} 元
                  </div>
                </div>
              }
            />
          )}
        </Space>
      </Modal>
    </div>
  );
}
